import { motion } from "framer-motion";

const Hero = () => {
  return (
    <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <span className="text-sm font-medium text-teal-400 bg-teal-400/10 px-4 py-2 rounded-full">
              Trusted by 2,000+ users
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6"
          >
            Connect, Focus, and{" "}
            <span className="bg-gradient-to-r from-teal-400 to-blue-500 bg-clip-text text-transparent">
              Thrive
            </span>{" "}
            with Sera
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-slate-400 mb-8 max-w-3xl mx-auto"
          >
            Download Sera to batch notifications intelligently and maintain focus effortlessly. Reduce notification noise and stay focused on what matters most.
          </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex justify-center"
              >
                <button className="group hover:scale-110 transition-all duration-300 ease-out p-2 rounded-xl bg-gradient-to-r from-teal-400 to-blue-500 hover:from-teal-300 hover:to-blue-400 shadow-lg hover:shadow-xl">
                  <img 
                    src="/GetItOnGooglePlay_Badge_Web_color_English.png" 
                    alt="Get it on Google Play" 
                    className="h-12 w-auto rounded-lg group-hover:drop-shadow-[0_15px_35px_rgba(20,184,166,0.5)] group-hover:drop-shadow-[0_8px_20px_rgba(59,130,246,0.4)] group-hover:brightness-110 transition-all duration-300"
                  />
                </button>
              </motion.div>
        </div>

        {/* App Screenshots and Visual Explanation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="relative max-w-7xl mx-auto"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            {/* Left Phone Screenshot - Hidden on mobile */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="relative justify-self-center lg:justify-self-end hidden lg:block"
            >
              <div className="w-64 h-[500px] rounded-[2.75rem] bg-slate-900 p-[10px] shadow-[0_30px_60px_rgba(2,6,23,0.6)] ring-1 ring-slate-700/60">
                <div className="relative w-full h-full rounded-[2.25rem] overflow-hidden border border-slate-700/70 bg-black">
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-1.5 bg-slate-700 rounded-full z-10"></div>
                  <img
                    src="/app-screens/app_inbox.svg"
                    alt="Sera app inbox screen"
                    className="w-full h-full object-cover bg-white"
                  />
                </div>
              </div>
            </motion.div>

            {/* Center Visual Explanation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.1 }}
              className="text-center space-y-6 lg:col-span-1 col-span-1"
            >
              {/* How Sera Works */}
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-white mb-4">How Sera Works</h3>
                
                {/* Step 1 */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.3, duration: 0.5 }}
                  className="flex items-center space-x-4"
                >
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                  <div className="text-left">
                    <p className="text-white font-semibold">Notifications arrive</p>
                    <p className="text-slate-400 text-sm">Apps send notifications to your phone</p>
                  </div>
                </motion.div>

                {/* Arrow */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5, duration: 0.5 }}
                  className="flex justify-center"
                >
                  {/* <div className="w-0.5 h-8 bg-gradient-to-b from-teal-400 to-blue-500"></div> */}
                </motion.div>

                {/* Step 2 */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.7, duration: 0.5 }}
                  className="flex items-center space-x-4"
                >
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                  <div className="text-left">
                    <p className="text-white font-semibold">Sera intercepts the notification</p>
                    <p className="text-slate-400 text-sm">Important notifications are delivered immediately</p>
                  </div>
                </motion.div>

                {/* Arrow */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.9, duration: 0.5 }}
                  className="flex justify-center"
                >
                  {/* <div className="w-0.5 h-8 bg-gradient-to-b from-teal-400 to-blue-500"></div> */}
                </motion.div>

                {/* Step 3 */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 2.1, duration: 0.5 }}
                  className="flex items-center space-x-4"
                >
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                  <div className="text-left">
                    <p className="text-white font-semibold">Notifications are delivered on your schedule</p>
                    <p className="text-slate-400 text-sm">Reducing interruptive noise and maintaining focus</p>
                  </div>
                </motion.div>
              </div>

              {/* Key Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.3, duration: 0.5 }}
                className="bg-slate-800/50 rounded-2xl p-6 space-y-3"
              >
                <h4 className="text-lg font-semibold text-white">Key Benefits</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                    <span className="text-slate-300">Stay focused without interruptions</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                    <span className="text-slate-300">Choose when to receive notifications</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                    <span className="text-slate-300">Priority apps still break through</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Phone Screenshot */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="relative justify-self-center lg:justify-self-start"
            >
              <div className="w-64 h-[500px] rounded-[2.75rem] bg-slate-900 p-[10px] shadow-[0_30px_60px_rgba(2,6,23,0.6)] ring-1 ring-slate-700/60">
                <div className="relative w-full h-full rounded-[2.25rem] overflow-hidden border border-slate-700/70 bg-black">
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-1.5 bg-slate-700 rounded-full z-10"></div>
                  <img
                    src="/app-screens/app_schedule.svg"
                    alt="Sera app schedule screen"
                    className="w-full h-full object-cover bg-white"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;